import { Fragment, useEffect, useState } from 'react'
import Modal from 'react-bootstrap/Modal'
import { CardBody, Row, Col, Table } from 'reactstrap'
import { API_URL } from './utils/Constants/URLS'
import { Link, useParams } from 'react-router-dom'
import PageLoader from './utils/PageLoader'
import { useSelector } from 'react-redux'

const StoreDetails = () => {
  const token = localStorage.getItem('token')
  const userDetails = useSelector(store => store.user)
  const { storeid } = useParams()

  // UI states
  const [showSuccessModal, setShowSuccessModal] = useState(false)
  const [showDeclineModal, setShowDeclineModal] = useState(false)
  const [modalMessage, setModalMessage] = useState('')
  const [pageLoader, setPageLoader] = useState(false)

  // Pagination
  const [pageNumber, setPageNumber] = useState(1)
  const [totalPages, setTotalPages] = useState(1)

  // Search (with debounce)
  const [searchValue, setSearchValue] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [searchType, setSearchType] = useState('partCode') // 'partCode' | 'modelName'

  // Data
  const [models, setModels] = useState([])

  /* ------------------------------------
     Debounce search (1.5 seconds)
  ------------------------------------ */
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchValue)
      setPageNumber(1) // reset pagination on new search
    }, 1500)

    return () => clearTimeout(timer)
  }, [searchValue])

  // Reset search value when search type changes
  useEffect(() => {
    setSearchValue('')
    setDebouncedSearch('')
    setPageNumber(1)
  }, [searchType])

  /* ------------------------------------
     Fetch models
  ------------------------------------ */
  useEffect(() => {
    const fetchModelsForBatches = async () => {
      setPageLoader(true)
      try {
        const searchParam = searchType === 'modelName' ? `modelName=${debouncedSearch}` : `partCode=${debouncedSearch}`
        const url = `${API_URL}/models/batches/stores/${storeid}?page=${pageNumber}&limit=150&${searchParam}`
        const res = await fetch(url, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          }
        })

        const result = await res.json()
        setModels(result?.data?.docs || [])
        setTotalPages(Math.ceil((result?.data?.total || 0) / 150))
      } catch (error) {
        console.error(error)
      } finally {
        setPageLoader(false)
      }
    }

    fetchModelsForBatches()
  }, [token, pageNumber, debouncedSearch, storeid, searchType])

  /* ------------------------------------
     Modal handler
  ------------------------------------ */
  const handleCloseModal = () => {
    setShowSuccessModal(false)
    setShowDeclineModal(false)
    window.location.reload()
  }

  return (
    <Fragment>
      <div className='p-4'>
        {/* Search */}
        <CardBody className='bg-white p-3'>
          <Row>
            <Col lg={2} md={3} sm={12} className='mb-2 mb-md-0'>
              <select
                className='form-select'
                value={searchType}
                onChange={e => setSearchType(e.target.value)}
              >
                <option value='partCode'>Part Code</option>
                <option value='modelName'>Model Name</option>
              </select>
            </Col>
            <Col lg={3} md={4} sm={12}>
              <input
                className='form-control'
                type='text'
                placeholder={searchType === 'modelName' ? 'Model Name' : 'Part Code'}
                value={searchValue}
                onChange={e => setSearchValue(e.target.value)}
              />
            </Col>
          </Row>
        </CardBody>

        {/* Table */}
        <CardBody>
          {pageLoader ? (
            <PageLoader />
          ) : (
            <div className='sm:overflow-auto sm:max-h-[400px] w-full mb-3'>
              <Table hover className='align-middle table-nowrap'>
                <thead className='table-light'>
                  <tr>
                    <th>Business Line</th>
                    <th>Product Line</th>
                    <th>Model Name</th>
                    <th>Part Code</th>
                    <th>UOM</th>
                    <th>Stock Qty</th>

                    {(userDetails.role === 'super_admin' ||
                      userDetails.role === 'admin') && (
                        <>
                          <th>Purchase Price / Unit</th>
                          <th>Total Purchase Price</th>
                        </>
                      )}

                    <th>List Price / Unit</th>
                  </tr>
                </thead>

                <tbody>
                  {models.length > 0 ? (
                    models.map((data, index) => (
                      <tr key={index}>
                        <td>{data?.model?.businessLine?.name}</td>
                        <td>{data?.model?.productLine?.productLineName}</td>
                        <td>
                          <Link
                            to={`${data?.model?._id}`}
                            state={{
                              modelName: data?.model?.modelName,
                              partCode: data?.model?.partCode
                            }}
                            className='text-dark no-underline'
                          >
                            {data?.model?.modelName}
                          </Link>
                        </td>
                        <td>{data?.model?.partCode}</td>
                        <td>{data?.model?.uom}</td>
                        <td>{data?.totalQuantity}</td>

                        {(userDetails.role === 'super_admin' ||
                          userDetails.role === 'admin') && (
                            <>
                              <td>
                                {Number(data?.purchasePrice || 0).toLocaleString(
                                  'en-US',
                                  { minimumFractionDigits: 2 }
                                )}
                              </td>
                              <td>
                                {(
                                  Number(data?.purchasePrice || 0) *
                                  Number(data?.totalQuantity || 0)
                                ).toLocaleString('en-US', {
                                  minimumFractionDigits: 2
                                })}
                              </td>
                            </>
                          )}

                        <td>
                          {Number(data?.listPrice || 0).toLocaleString(
                            'en-US',
                            { minimumFractionDigits: 2 }
                          )}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan='9' className='text-center text-muted'>
                        No models found
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
          )}
        </CardBody>

        {/* Pagination */}
        <CardBody className='float-right'>
          <nav>
            <ul className='pagination'>
              <li className={`page-item ${pageNumber === 1 ? 'disabled' : ''}`}>
                <button
                  className='page-link'
                  onClick={() => setPageNumber(p => Math.max(1, p - 1))}
                >
                  Previous
                </button>
              </li>

              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter(
                  p =>
                    p === 1 ||
                    p === totalPages ||
                    (p >= pageNumber - 2 && p <= pageNumber + 2)
                )
                .map((p, i, arr) => {
                  if (i > 0 && arr[i] - arr[i - 1] > 1) {
                    return (
                      <li key={`dots-${p}`} className='page-item disabled'>
                        <span className='page-link'>...</span>
                      </li>
                    )
                  }
                  return (
                    <li
                      key={p}
                      className={`page-item ${p === pageNumber ? 'active' : ''
                        }`}
                    >
                      <button
                        className='page-link'
                        onClick={() => setPageNumber(p)}
                      >
                        {p}
                      </button>
                    </li>
                  )
                })}

              <li
                className={`page-item ${pageNumber === totalPages ? 'disabled' : ''
                  }`}
              >
                <button
                  className='page-link'
                  onClick={() =>
                    setPageNumber(p => Math.min(totalPages, p + 1))
                  }
                >
                  Next
                </button>
              </li>
            </ul>
          </nav>
        </CardBody>

        {/* Success Modal */}
        <Modal show={showSuccessModal} centered>
          <Modal.Body className='text-center'>
            <h4>Job Done</h4>
            <p>{modalMessage}</p>
            <button
              className='btn btn-success w-100'
              onClick={handleCloseModal}
            >
              Okay
            </button>
          </Modal.Body>
        </Modal>

        {/* Error Modal */}
        <Modal show={showDeclineModal} centered>
          <Modal.Body className='text-center'>
            <h4>Error Occurred</h4>
            <p>Something went wrong!</p>
            <button
              className='btn btn-danger w-100'
              onClick={handleCloseModal}
            >
              Okay
            </button>
          </Modal.Body>
        </Modal>
      </div>
    </Fragment>
  )
}

export default StoreDetails